import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import Tabs from "components/molecules/Tabs";
import { AIReview, Patient, getPromptTypeKoreanName } from "common/models/types";

type TabType = "pinned" | "recent";

interface AIReviewManagementTemplateProps {
  tab: TabType;
  page: number;
  pageSize: number;
  total: number;
  pinnedCount?: number;
  recentCount?: number;
  items: AIReview[];
  patients: { [id: number]: Patient };
  isLoading: boolean;
  isError: boolean;
  isPinLoading: boolean;
  onTabChange: (tab: TabType) => void;
  onPageChange: (page: number) => void;
  onReportClick: (review: AIReview) => void;
  onTogglePin: (reviewId: number) => void;
}

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 40px 60px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const ContentArea = styled.div`
  margin-top: 24px;
  background-color: white;
  border-radius: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  padding: 24px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Skyblue")?.[200]};
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionArea = styled.div`
  flex-shrink: 0;
  margin-left: 16px;
`;

const PinButton = styled.button`
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 12px;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  margin-top: 16px;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid
    ${({ active }) => (active ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[200])};
  background: ${({ active }) => (active ? COLOR_SYSTEM.get("Blue")?.[400] : "#fff")};
  color: ${({ active }) =>
    active ? "#fff" : COLOR_SYSTEM.get("Gray")?.[700]};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 36px;

  &:hover:not(:disabled) {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    color: ${({ active }) => (active ? "#fff" : COLOR_SYSTEM.get("Blue")?.[400])};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfo = styled.span`
  margin: 0 12px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const formatDate = (date?: Date | string): string => {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getPageNumbers = (currentPage: number, totalPages: number): number[] => {
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  return pages;
};

const buildTabs = (pinnedCount?: number, recentCount?: number) => [
  {
    id: "pinned",
    label: pinnedCount !== undefined ? `고정한 보고서 (${pinnedCount})` : "고정한 보고서",
  },
  {
    id: "recent",
    label: recentCount !== undefined ? `최근 보고서 (${recentCount})` : "최근 보고서",
  },
];

const AIReviewManagementTemplate: React.FC<AIReviewManagementTemplateProps> = ({
  tab,
  page,
  pageSize,
  total,
  pinnedCount,
  recentCount,
  items,
  patients,
  isLoading,
  isError,
  isPinLoading,
  onTabChange,
  onPageChange,
  onReportClick,
  onTogglePin,
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const hasData = items.length > 0;
  const tabs = buildTabs(pinnedCount, recentCount);

  const renderEmpty = () => {
    const message =
      tab === "pinned"
        ? "고정한 보고서가 없습니다. 보고서 상세에서 📌 버튼으로 고정할 수 있습니다."
        : "아직 생성된 보고서가 없습니다.";
    return (
      <EmptyState>
        <Icon
          name={tab === "pinned" ? "Pin 02 Filled" : "Document"}
          size={48}
          color={COLOR_SYSTEM.get("Gray")?.[300]}
        />
        <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
          {message}
        </Text>
      </EmptyState>
    );
  };

  const renderError = () => (
    <EmptyState>
      <Icon
        name="Exclamation Mark Circle Filled"
        size={48}
        color={COLOR_SYSTEM.get("Red")?.[400]}
      />
      <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
        데이터를 불러오는 중 오류가 발생했습니다.
      </Text>
    </EmptyState>
  );

  const renderItem = (review: AIReview) => {
    const isPinned = !!review.pinnedAt;
    const patient = review.patientId ? patients[review.patientId] : undefined;
    const patientLabel = review.patientId
      ? `환자 #${review.patientId}${patient?.memo ? ` (${patient.memo})` : ""}`
      : "환자 정보 없음";

    return (
      <ListItem key={review.id} onClick={() => onReportClick(review)}>
        <InfoSection>
          <InfoRow>
            <Text textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")?.[900]}>
              보고서 #{review.id}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              {patientLabel}
            </Text>
          </InfoRow>
          <InfoRow>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              생성일: {formatDate(review.reviewDate || review.createdAt)}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              종류: {getPromptTypeKoreanName(review.promptType)}
            </Text>
            {isPinned && (
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                핀한일자: {formatDate(review.pinnedAt)}
              </Text>
            )}
          </InfoRow>
          {review.memo && (
            <InfoRow>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Skyblue")?.[500]}>
                메모: {review.memo}
              </Text>
            </InfoRow>
          )}
        </InfoSection>
        <ActionArea>
          <PinButton
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(review.id);
            }}
            disabled={isPinLoading}
            aria-label={isPinned ? "고정 해제" : "고정"}
          >
            <Icon
              name={isPinned ? "Pin 02 Filled" : "Pin 02"}
              size={20}
              color={
                isPinned
                  ? COLOR_SYSTEM.get("Yellow")?.[500]
                  : COLOR_SYSTEM.get("Gray")?.[400]
              }
            />
          </PinButton>
        </ActionArea>
      </ListItem>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = getPageNumbers(page, totalPages);
    return (
      <PaginationContainer>
        <PageButton onClick={() => onPageChange(page - 1)} disabled={page === 1}>
          <Icon name="Chevron Left" size={16} />
        </PageButton>
        {pages.map((p) => (
          <PageButton key={p} active={p === page} onClick={() => onPageChange(p)}>
            {p}
          </PageButton>
        ))}
        <PageInfo>
          {page} / {totalPages}
        </PageInfo>
        <PageButton onClick={() => onPageChange(page + 1)} disabled={page === totalPages}>
          <Icon name="Chevron Right" size={16} />
        </PageButton>
      </PaginationContainer>
    );
  };

  const renderBody = () => {
    if (isLoading && items.length === 0) {
      return (
        <LoadingState>
          <Icon name="Spinner" size={32} color={COLOR_SYSTEM.get("Blue")?.[400]} />
        </LoadingState>
      );
    }
    if (isError) return renderError();
    if (!hasData) return renderEmpty();
    return <ListContainer>{items.map(renderItem)}</ListContainer>;
  };

  return (
    <Container>
      <Header>
        <Icon name="Document" size={24} color={COLOR_SYSTEM.get("Gray")?.[900]} />
        <Text textType="H1" color={COLOR_SYSTEM.get("Gray")?.[900]}>
          보고서 관리
        </Text>
      </Header>

      <Tabs tabs={tabs} activeTab={tab} onTabChange={(t) => onTabChange(t as TabType)} />

      <ContentArea>
        {renderBody()}
        {renderPagination()}
      </ContentArea>
    </Container>
  );
};

export default AIReviewManagementTemplate;
